import type { CreateShipmentSuccessResponse } from '../../../core/api/shipment/models/create-shipment.model';
import {
  type CompletedShipmentConfirmation,
  type CompletedShipmentDocumentDisplay,
  type CompletedShipmentInformation,
  type CompletedShipmentPaymentDisplay,
  type ShipmentInformationAdditionalServices,
  type ShipmentInformationAddress,
  type ShipmentInformationEstimate,
  type ShipmentInformationPackaging,
  type ShipmentInformationServiceDisplay,
} from '../../../core/api/shipment/models/completed-shipment.model';
import { formatLocalIsoDate } from '../../../shared/utils/ship-from-local-clock';

/** Shipment Manager docType for the printable shipping label. */
const LABEL_DOC_TYPE = 'DomesticBillOfLading';

/**
 * Selects the shipping-label document, which is absent when generation failed or is unrequested.
 */
function mapLabelDocument(
  response: CreateShipmentSuccessResponse,
): CompletedShipmentDocumentDisplay | undefined {
  const label = (response.processing.documents.documents ?? []).find(
    (document) => document.docType === LABEL_DOC_TYPE,
  );
  const documentId = label?.documentId?.trim();
  if (!label || !documentId) {
    return undefined;
  }

  return {
    documentId,
    docType: label.docType,
    status: label.status,
    docURL: label.docURL,
    failureMessage: label.failureMessage,
  };
}

/**
 * Copies a trimmed optional string when it is non-empty.
 *
 * @param value Raw SUCCESS field
 */
function optionalTrimmed(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

/**
 * Projects a Shipment Lambda address, keeping only modelled confirmation fields.
 *
 * @param address Address block from SUCCESS `shipmentInformation`
 */
function mapAddress(address: ShipmentInformationAddress): ShipmentInformationAddress {
  return {
    qualifier: address.qualifier,
    city: address.city,
    provinceState: address.provinceState,
    postalZipCode: address.postalZipCode,
    countryCode: address.countryCode,
    ...(address.residential === undefined ? {} : { residential: address.residential }),
    ...(optionalTrimmed(address.companyName)
      ? { companyName: optionalTrimmed(address.companyName) }
      : {}),
    ...(optionalTrimmed(address.attentionToName)
      ? { attentionToName: optionalTrimmed(address.attentionToName) }
      : {}),
    ...(optionalTrimmed(address.department)
      ? { department: optionalTrimmed(address.department) }
      : {}),
    ...(optionalTrimmed(address.streetAddress1)
      ? { streetAddress1: optionalTrimmed(address.streetAddress1) }
      : {}),
    ...(optionalTrimmed(address.streetAddress2)
      ? { streetAddress2: optionalTrimmed(address.streetAddress2) }
      : {}),
    ...(optionalTrimmed(address.streetAddress3)
      ? { streetAddress3: optionalTrimmed(address.streetAddress3) }
      : {}),
    ...(optionalTrimmed(address.suiteAptUnit)
      ? { suiteAptUnit: optionalTrimmed(address.suiteAptUnit) }
      : {}),
    ...(optionalTrimmed(address.phoneNumber)
      ? { phoneNumber: optionalTrimmed(address.phoneNumber) }
      : {}),
    ...(optionalTrimmed(address.email) ? { email: optionalTrimmed(address.email) } : {}),
    ...(optionalTrimmed(address.extension)
      ? { extension: optionalTrimmed(address.extension) }
      : {}),
    ...(optionalTrimmed(address.entryBuzzerCode)
      ? { entryBuzzerCode: optionalTrimmed(address.entryBuzzerCode) }
      : {}),
  };
}

/**
 * Projects packaging confirmation fields, dropping unmodelled keys.
 *
 * @param packaging Packaging block from SUCCESS `shipmentInformation`
 */
function mapPackaging(packaging: ShipmentInformationPackaging): ShipmentInformationPackaging {
  const types = packaging.specialHandlingTypes?.map((type) => ({
    code: type.code.trim(),
    displayName: type.displayName.trim(),
  }));

  return {
    packageTypeDescription: packaging.packageTypeDescription,
    totalPieces: packaging.totalPieces,
    totalWeight: packaging.totalWeight,
    specialHandling: packaging.specialHandling,
    additionalHandling: packaging.additionalHandling,
    ...(packaging.weightUnit ? { weightUnit: packaging.weightUnit } : {}),
    ...(types?.length ? { specialHandlingTypes: types } : {}),
    ...(packaging.additionalHandlingCount === undefined
      ? {}
      : { additionalHandlingCount: packaging.additionalHandlingCount }),
  };
}

/**
 * Projects additional-services confirmation fields, dropping unmodelled keys.
 *
 * @param additionalServices Additional-services block from SUCCESS `shipmentInformation`
 */
function mapAdditionalServices(
  additionalServices: ShipmentInformationAdditionalServices,
): ShipmentInformationAdditionalServices {
  return {
    declaredValue: additionalServices.declaredValue,
    signatureRequired: additionalServices.signatureRequired,
    ...(additionalServices.signatureType === undefined
      ? {}
      : { signatureType: additionalServices.signatureType }),
    ...(optionalTrimmed(additionalServices.packageDescription)
      ? { packageDescription: optionalTrimmed(additionalServices.packageDescription) }
      : {}),
  };
}

/**
 * Projects the rated service display name used on Get Labels / print.
 *
 * @param service Service block from SUCCESS `shipmentInformation`
 */
function mapService(
  service: ShipmentInformationServiceDisplay | undefined,
): ShipmentInformationServiceDisplay | undefined {
  const productCode = optionalTrimmed(service?.productCode);
  const displayName = optionalTrimmed(service?.displayName);
  if (!productCode || !displayName) {
    return undefined;
  }

  return { productCode, displayName };
}

/**
 * Copies peak / multi-piece / holiday surcharge amounts and drops unmodelled keys (OM-735).
 *
 * @param surcharges SUCCESS estimate surcharge block
 */
function mapEstimateSurcharges(
  surcharges: ShipmentInformationEstimate['surcharges'],
): NonNullable<ShipmentInformationEstimate['surcharges']> | undefined {
  if (!surcharges) {
    return undefined;
  }

  return {
    peak: surcharges.peak,
    multiPiece: surcharges.multiPiece,
    holiday: surcharges.holiday,
  };
}

/**
 * Copies rated accessorial lines (SH, LDD, Beyond) and drops unmodelled keys (OM-735).
 *
 * @param accessorialCharges SUCCESS estimate accessorial list
 */
function mapEstimateAccessorialCharges(
  accessorialCharges: ShipmentInformationEstimate['accessorialCharges'],
): NonNullable<ShipmentInformationEstimate['accessorialCharges']> | undefined {
  if (!accessorialCharges) {
    return undefined;
  }

  return accessorialCharges.map((charge) => ({
    productCode: charge.productCode,
    amount: charge.amount,
  }));
}

/**
 * Copies per-SH peak surcharge lines so Get Labels can show Peak Large / Peak Oversize
 * instead of a single aggregated Peak Surcharge row (OM-724).
 *
 * @param peakSurcharges SUCCESS estimate peak breakdown
 */
function mapEstimatePeakSurcharges(
  peakSurcharges: ShipmentInformationEstimate['peakSurcharges'],
): NonNullable<ShipmentInformationEstimate['peakSurcharges']> | undefined {
  if (!peakSurcharges?.length) {
    return undefined;
  }

  return peakSurcharges.map((charge) => ({
    productCode: charge.productCode,
    amount: charge.amount,
  }));
}

/**
 * Projects pickup confirmation fields used on Get Labels / print.
 *
 * @param pickup Pickup block from SUCCESS `shipmentInformation`
 */
function mapPickup(
  pickup: CompletedShipmentInformation['pickup'],
): CompletedShipmentInformation['pickup'] | undefined {
  if (!pickup) {
    return undefined;
  }

  return {
    status: pickup.status,
    ...(optionalTrimmed(pickup.pickupId) ? { pickupId: optionalTrimmed(pickup.pickupId) } : {}),
    ...(optionalTrimmed(pickup.confirmationNumber)
      ? { confirmationNumber: optionalTrimmed(pickup.confirmationNumber) }
      : {}),
    ...(optionalTrimmed(pickup.pickupDate)
      ? { pickupDate: optionalTrimmed(pickup.pickupDate) }
      : {}),
    ...(optionalTrimmed(pickup.readyTime) ? { readyTime: optionalTrimmed(pickup.readyTime) } : {}),
    ...(optionalTrimmed(pickup.closeTime) ? { closeTime: optionalTrimmed(pickup.closeTime) } : {}),
    ...(optionalTrimmed(pickup.locationCode)
      ? { locationCode: optionalTrimmed(pickup.locationCode) }
      : {}),
    ...(optionalTrimmed(pickup.failureMessage)
      ? { failureMessage: optionalTrimmed(pickup.failureMessage) }
      : {}),
  };
}

/**
 * Projects the Shipment Lambda confirmation facts field by field so unmodelled backend
 * keys (payment and CSOP internals) can never reach browser storage.
 */
function mapShipmentInformation(
  raw: Record<string, unknown>,
): CompletedShipmentInformation | undefined {
  const info = raw as Partial<CompletedShipmentInformation>;
  const addresses = info.addresses;
  const estimate = info.estimate;
  if (!info.shipmentPin?.trim() || !addresses?.shipFrom || !addresses.shipTo || !estimate) {
    return undefined;
  }

  const service = mapService(info.service);
  const pickup = mapPickup(info.pickup);
  const surcharges = mapEstimateSurcharges(estimate.surcharges);
  const peakSurcharges = mapEstimatePeakSurcharges(estimate.peakSurcharges);
  const accessorialCharges = mapEstimateAccessorialCharges(estimate.accessorialCharges);

  return {
    shipmentPin: info.shipmentPin,
    shipmentDate: info.shipmentDate ?? '',
    expectedDeliveryDate: info.expectedDeliveryDate ?? '',
    productCode: info.productCode ?? '',
    ...(service ? { service } : {}),
    billingMethod: info.billingMethod ?? 'CREDIT_CARD',
    language: info.language ?? 'en',
    addresses: {
      shipFrom: mapAddress(addresses.shipFrom),
      shipTo: mapAddress(addresses.shipTo),
      returnTo: addresses.returnTo ? mapAddress(addresses.returnTo) : null,
    },
    estimate: {
      currency: estimate.currency,
      shipmentCharge: estimate.shipmentCharge,
      fuelSurchargeAmount: estimate.fuelSurchargeAmount,
      totalCharge: estimate.totalCharge,
      gstTaxAmount: estimate.gstTaxAmount,
      pstTaxAmount: estimate.pstTaxAmount,
      hstTaxAmount: estimate.hstTaxAmount,
      qstTaxAmount: estimate.qstTaxAmount,
      ...(surcharges ? { surcharges } : {}),
      ...(peakSurcharges ? { peakSurcharges } : {}),
      ...(accessorialCharges ? { accessorialCharges } : {}),
    },
    ...(info.packaging ? { packaging: mapPackaging(info.packaging) } : {}),
    ...(info.additionalServices
      ? { additionalServices: mapAdditionalServices(info.additionalServices) }
      : {}),
    ...(info.pickupType ? { pickupType: info.pickupType } : {}),
    ...(pickup ? { pickup } : {}),
  };
}

/**
 * Keeps the Payment Method / Billing Address card off the page unless the backend
 * returned a card summary, so no placeholder payment data is ever shown.
 */
function mapPaymentDisplay(
  response: CreateShipmentSuccessResponse,
): CompletedShipmentPaymentDisplay | undefined {
  const display = response.paymentDisplay;
  const maskedPan = display?.maskedPan?.trim();
  if (!display || !maskedPan) {
    return undefined;
  }

  return {
    ...(display.cardBrand ? { cardBrand: display.cardBrand } : {}),
    maskedPan,
    expiry: display.expiry?.trim() ?? '',
    billingName: display.billingName?.trim() ?? '',
    billingPostalCode: display.billingPostalCode?.trim() ?? '',
  };
}

/**
 * Prefers confirmationNumber, then pickupConfirmationNumber; empty after trim is ignored.
 */
function readTrimmedConfirmationField(record: Record<string, unknown>): string {
  const confirmationNumber = record['confirmationNumber'];
  if (typeof confirmationNumber === 'string') {
    const trimmed = confirmationNumber.trim();
    if (trimmed) {
      return trimmed;
    }
  }

  const pickupConfirmationNumber = record['pickupConfirmationNumber'];
  if (typeof pickupConfirmationNumber === 'string') {
    return pickupConfirmationNumber.trim();
  }

  return '';
}

/**
 * Reads pickup confirmation from create SUCCESS processing / shipmentInformation when present.
 */
function readPickupConfirmationNumber(response: CreateShipmentSuccessResponse): string | undefined {
  const processingPickup = response.processing?.pickup;
  if (processingPickup && typeof processingPickup === 'object') {
    const fromProcessing = readTrimmedConfirmationField(
      processingPickup as Record<string, unknown>,
    );
    if (fromProcessing) {
      return fromProcessing;
    }
  }

  const info = response.shipment.shipmentInformation;
  const pickup = info?.['pickup'];
  if (pickup && typeof pickup === 'object' && !Array.isArray(pickup)) {
    const confirmation = readTrimmedConfirmationField(pickup as Record<string, unknown>);
    if (confirmation) {
      return confirmation;
    }
  }

  return undefined;
}

/**
 * Adapts authoritative Shipment Manager success facts to the existing Get Labels state.
 * CSOP internals are intentionally excluded from the persisted UI projection.
 * Cancel deadline uses the Ship From province creation calendar day (OM-891 / OM-893),
 * not the browser clock and not service `shipmentDate`.
 */
export function mapCreateSuccessToCompletedShipment(
  response: CreateShipmentSuccessResponse,
  now: Date = new Date(),
): CompletedShipmentConfirmation {
  const pickupConfirmationNumber = readPickupConfirmationNumber(response);
  const labelDocument = mapLabelDocument(response);
  const shipmentInformation = mapShipmentInformation(response.shipment.shipmentInformation);
  const paymentDisplay = mapPaymentDisplay(response);
  const shipFromProvince =
    shipmentInformation?.addresses?.shipFrom?.provinceState?.trim() ||
    readRawShipFromProvince(response);
  const shipmentCreationDate = formatLocalIsoDate(now, {
    provinceCode: shipFromProvince || null,
  });

  return {
    trackingPin: response.shipment.shipmentPin,
    shipmentCreationDate,
    cancelDeadlineLocal: `${shipmentCreationDate}T23:59:59.999`,
    confirmationEmailSent: false,
    shipmentId: response.shipment.shipmentId,
    ...(pickupConfirmationNumber ? { pickupConfirmationNumber } : {}),
    ...(paymentDisplay ? { paymentDisplay } : {}),
    ...(shipmentInformation ? { shipmentInformation } : {}),
    ...(labelDocument ? { labelDocument } : {}),
  };
}

/**
 * Reads Ship From province from SUCCESS payload when full address projection is unavailable.
 */
function readRawShipFromProvince(response: CreateShipmentSuccessResponse): string | null {
  const info = response.shipment.shipmentInformation;
  if (!info || typeof info !== 'object' || Array.isArray(info)) {
    return null;
  }
  const addresses = (info as Record<string, unknown>)['addresses'];
  if (!addresses || typeof addresses !== 'object' || Array.isArray(addresses)) {
    return null;
  }
  const shipFrom = (addresses as Record<string, unknown>)['shipFrom'];
  if (!shipFrom || typeof shipFrom !== 'object' || Array.isArray(shipFrom)) {
    return null;
  }
  const province = (shipFrom as Record<string, unknown>)['provinceState'];
  return typeof province === 'string' && province.trim() ? province.trim() : null;
}
